import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import odooApi from '../services/odooApi';
import {
  getDefaultTranslations,
  normalizeLanguageCode,
  supportedLanguages as fallbackLanguages,
} from '../translations/defaultTranslations';

const ODOO_API_URL = (
  process.env.REACT_APP_ODOO_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'https://admin.velaris-royal.com/api/v1'
).replace(/\/+$/, '');

const LanguageContext = createContext();

const FLAG_EMOJI_TO_CODE = {
  '🇬🇧': 'gb',
  '🇦🇪': 'ae',
  '🇫🇷': 'fr',
  '🇩🇪': 'de',
  '🇷🇺': 'ru',
};

const normalizeLanguageFlag = (flag) => {
  if (!flag) {
    return 'gb';
  }

  if (FLAG_EMOJI_TO_CODE[flag]) {
    return FLAG_EMOJI_TO_CODE[flag];
  }

  return String(flag).toLowerCase();
};

const buildLanguageMap = (languages) => {
  const merged = new Map();

  fallbackLanguages.forEach((language) => {
    merged.set(language.code, {
      code: language.code,
      name: language.name,
      nativeName: language.nativeName,
      native_name: language.nativeName,
      direction: language.direction,
      flag: normalizeLanguageFlag(language.flag),
    });
  });

  languages.forEach((language) => {
    const normalizedCode = normalizeLanguageCode(language.code);
    const existing = merged.get(normalizedCode) || {};

    merged.set(normalizedCode, {
      ...existing,
      code: normalizedCode,
      name: language.name || existing.name || normalizedCode,
      nativeName: language.nativeName || language.native_name || existing.nativeName || existing.name || language.name || normalizedCode,
      native_name: language.native_name || language.nativeName || existing.native_name || existing.nativeName || language.name || normalizedCode,
      direction: language.direction === 'rtl' ? 'rtl' : existing.direction || 'ltr',
      flag: normalizeLanguageFlag(language.flag || existing.flag),
    });
  });

  return Array.from(merged.values());
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return normalizeLanguageCode(localStorage.getItem('velaris_language') || 'en');
  });
  const [languages, setLanguages] = useState(() => buildLanguageMap([]));
  const [translations, setTranslations] = useState(() => getDefaultTranslations('en'));
  const [isRTL, setIsRTL] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await fetch(`${ODOO_API_URL}/languages`);
        if (response.ok) {
          const data = await response.json();
          if (data.languages && data.languages.length > 0) {
            setLanguages(buildLanguageMap(data.languages));
          }
        }
      } catch (error) {
        console.error('Failed to fetch languages from Odoo:', error);
      }
    };

    fetchLanguages();
  }, []);

  useEffect(() => {
    const fetchTranslations = async () => {
      setIsLoading(true);
      const shortCode = normalizeLanguageCode(currentLanguage);
      const localDefaults = getDefaultTranslations(shortCode);

      try {
        const response = await fetch(`${ODOO_API_URL}/translations/${shortCode}`);
        if (response.ok) {
          const data = await response.json();
          if (data && Object.keys(data).length > 0) {
            if (shortCode !== 'en' && data['nav.home'] === 'Home' && localDefaults['nav.home'] !== 'Home') {
              setTranslations(localDefaults);
            } else {
              setTranslations({ ...localDefaults, ...data });
            }
          } else {
            setTranslations(localDefaults);
          }
        } else {
          setTranslations(localDefaults);
        }
      } catch (error) {
        console.error('Failed to fetch translations from Odoo:', error);
        setTranslations(localDefaults);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTranslations();
    odooApi.setLanguage(normalizeLanguageCode(currentLanguage));
  }, [currentLanguage]);

  useEffect(() => {
    const normalizedCode = normalizeLanguageCode(currentLanguage);
    const currentLangInfo = languages.find((language) => language.code === normalizedCode) || languages[0];
    const nextDirection = currentLangInfo?.direction === 'rtl' ? 'rtl' : 'ltr';
    const rtl = nextDirection === 'rtl';

    setIsRTL(rtl);
    document.documentElement.dir = nextDirection;
    document.documentElement.lang = normalizedCode;
    document.documentElement.setAttribute('data-language', normalizedCode);
    document.body.dir = nextDirection;
    document.body.classList.toggle('rtl', rtl);
    document.body.classList.toggle('ltr', !rtl);
    document.body.style.direction = nextDirection;
    document.body.style.textAlign = rtl ? 'right' : 'left';

    if (!languages.some((language) => language.code === normalizedCode)) {
      setCurrentLanguage('en');
      localStorage.setItem('velaris_language', 'en');
    }
  }, [languages, currentLanguage]);

  const changeLanguage = useCallback((langCode) => {
    const normalizedCode = normalizeLanguageCode(langCode);
    setCurrentLanguage(normalizedCode);
    localStorage.setItem('velaris_language', normalizedCode);
  }, []);

  const t = useCallback((key, fallback = null) => {
    return translations[key] || fallback || key;
  }, [translations]);

  const tf = useCallback((key, params = {}, fallback = null) => {
    let value = translations[key] || fallback || key;
    Object.entries(params || {}).forEach(([paramKey, paramValue]) => {
      value = value.replaceAll(`{${paramKey}}`, String(paramValue));
    });
    return value;
  }, [translations]);

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        languages,
        translations,
        isRTL,
        isLoading,
        changeLanguage,
        t,
        tf,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
